<template>
  <div class="relative inline-flex">
    <button
      ref="triggerRef"
      type="button"
      class="inline-flex size-8 items-center justify-center rounded-md border border-border bg-bg-surface text-sm transition-[color,border-color,background-color] hover:border-accent hover:bg-accent/12 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
      :aria-label="triggerLabel"
      :aria-expanded="open"
      aria-haspopup="listbox"
      @mousedown.prevent
      @click="toggleListbox"
      @keydown="onKeydown"
    >😊</button>
    <div
      v-if="open"
      ref="listRef"
      role="listbox"
      :aria-label="messages.chooseEmoji"
      class="absolute right-0 top-full z-20 mt-1 grid grid-cols-4 gap-1 rounded-md border border-border bg-bg-elevated p-1 shadow-panel"
      @keydown="onKeydown"
    >
      <button
        v-for="emoji in EMOJI_OPTIONS"
        :key="emoji"
        type="button"
        role="option"
        class="inline-flex size-8 items-center justify-center rounded-sm text-base transition-[background-color] hover:bg-accent/12 focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent"
        :aria-label="messages.insert(emoji)"
        @mousedown.prevent
        @click="selectEmoji(emoji)"
      >{{ emoji }}</button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { defineLocaleCatalog } from '@/shared/i18n'
import { useLocaleMessages } from '@/shared/composables/useI18n'
import { emojiPickerEn } from './locales/en.ts'
import { emojiPickerRu } from './locales/ru.ts'
import { useListbox } from '@/shared/composables'

export interface EmojiPickerButtonProps {
  /** Accessible label for the emoji chooser trigger. */
  readonly label?: string
}

const catalog = defineLocaleCatalog({
  en: emojiPickerEn,
  ru: emojiPickerRu,
})
const messages = useLocaleMessages(catalog)
const props = withDefaults(defineProps<EmojiPickerButtonProps>(), {
  label: '',
})
const triggerLabel = computed(() => props.label || messages.value.insertEmoji)

const emit = defineEmits<{
  (e: 'select', emoji: string): void
}>()

const EMOJI_OPTIONS: ReadonlyArray<string> = ['⚔️', '🛡️', '🏹', '🐔', '🌲', '👑', '🔥', '💬']

const {
  open,
  triggerRef,
  listRef,
  toggleListbox,
  onKeydown,
  select,
} = useListbox({
  onSelect: (emoji) => emit('select', emoji),
})

// Both refs are consumed by the template and by useListbox's click-outside
// lifecycle. Pin them here so noUnusedLocals recognises those bindings.
void triggerRef
void listRef

function selectEmoji(emoji: string): void {
  select(emoji)
}
</script>
